import { Component, Vue } from 'vue-property-decorator';
import appStore from '@/store/app';
import ordersStore from '@/store/orders';
import { Order, OrderLine } from '@/models';
import CardComponent from '@/components/CardComponent';
import CustomToolbar from '@/components/CustomToolbar';
import ColumnStatus from '@/components/ColumnStatus';
import { TypeOfOperation, BaseProduct, ProductStatus, DocumentStatus, Guid } from '@/types';

@Component({
   components: { CardComponent, CustomToolbar, ColumnStatus },
})
export default class Cart extends Vue {

   private ProductStatus: typeof ProductStatus = ProductStatus;

   private sortField = 'createdAt';
   private sortDirection = 'asc';
   private selected: OrderLine | null = null;

   get loading(): boolean {
      return appStore.isLoading;
   }

   get cart(): Order {
      return ordersStore.cart;
   }

   get canPrint(): boolean {
      return ordersStore.cart !== null && ordersStore.cart.id !== Guid.Empty;
   }

   get lines(): OrderLine[] {
      return this.cart.lines || [];
   }

   get totalNumberOfRows(): number {
      if (this.cart && this.cart.lines) {
         return this.cart.lines.length;
      }
      return 0;
   }

   get isTransmittable(): boolean {
      return this.cart.status === DocumentStatus.Open && this.lines.length > 0;
   }

   get cartStatusDescription(): string {
      const currentCart = this.cart;
      return currentCart.statusDescription || appStore.documentStatus()(currentCart.status).shortDescription;
   }

   private async created() {
      if (!ordersStore.isCartLoaded) {
         await ordersStore.fetchCartAsync({});
      }
   }

   private async mounted() {
      await appStore.activateMenuById({ rootId: 100, subMenuId: 102 });
   }

   private goBack() {
      this.$router.go(-1);
   }

   private async send() {
      this.$buefy.dialog.confirm({
         title: 'Trasmissione Ordine',
         message: 'Sei sicuro/a di voler <b>trasmettere</b> l\'ordine ?',
         confirmText: 'Conferma',
         cancelText: 'Annulla',
         type: 'is-info',
         hasIcon: true,
         onConfirm: async () => {
            await this.transmittOrderAsync();
         },
      });
   }

   private async print() {
      if (this.cart) {
         await ordersStore.printOrderAsync({ id: this.cart.id })
            .then((blob) => {
               if (!blob) {
                  return;
               }
               const url = URL.createObjectURL(new Blob([blob], {
                  type: 'application/pdf',
               }));
               const link = document.createElement('a');
               link.href = url;
               link.setAttribute('download', `Ordine-${this.cart.documentNumber}.pdf`);
               link.style.display = 'none';
               document.body.appendChild(link);
               link.click();
               document.body.removeChild(link);
            });
      }
   }

   private async transmittOrderAsync() {
      await ordersStore.sendCartAsync({ id: this.cart.id })
         .then((result) => {
            if (result) {
               this.$router.push({ name: 'ordersTransmitted' });
            }
         });
   }

   private async addQuantityToCartModal(line: OrderLine) {
      if (line && line.productType && line.pharmacode) {

         this.$buefy.dialog.prompt({
            confirmText: 'Conferma',
            cancelText: 'Annulla',
            type: 'is-info',
            title: `${line.pharmacode} ${line.name || ''}`,
            message: `Quantità ?`,
            inputAttrs: {
                type: 'number',
                placeholder: 'Inserisci Quantità',
                value: line.quantity,
                maxlength: 3,
                min: 1,
                max: 999,
            },
            trapFocus: true,
            onConfirm: async (value: string) => await this.addQuantityToCartYourQuantity(line, parseInt(value, 0)),
        });
      }
   }

   private async addQuantityToCartYourQuantity(line: OrderLine, quantity: number) {
      if (line && line.productType && line.pharmacode) {
         const product: BaseProduct = { type: line.productType, pharmacode: line.pharmacode, name: '' };
         await ordersStore.addProductToCartAsync({ product, quantity, operation: TypeOfOperation.ReplaceQuantity });
      }
   }

   private async addQuantityToCart(line: OrderLine) {
      if (line && line.productType && line.pharmacode) {
         const product: BaseProduct = { type: line.productType, pharmacode: line.pharmacode, name: '' };
         await ordersStore.addProductToCartAsync({ product, quantity: line.quantity + 1, operation: TypeOfOperation.ReplaceQuantity });
      }
   }

   private async removeQuantityFromCart(line: OrderLine) {
      if (line && line.productType && line.pharmacode) {
         const product: BaseProduct = { type: line.productType, pharmacode: line.pharmacode, name: '' };
         await ordersStore.addProductToCartAsync({ product, quantity: line.quantity - 1, operation: TypeOfOperation.ReplaceQuantity });
      }
   }

   private async removeProduct(line: OrderLine) {
      if (line && line.productType && line.pharmacode) {

         const product: BaseProduct = { type: line.productType, pharmacode: line.pharmacode, name: '' };

         this.$buefy.dialog.confirm({
            title: 'Eliminazione Articolo',
            message: 'Sei sicuro/a di voler <b>rimuovere</b> l\'articolo selezionato ?',
            confirmText: 'Conferma',
            cancelText: 'Annulla',
            type: 'is-warning',
            hasIcon: true,
            onConfirm: async () => await ordersStore.deleteProductInCartAsync({ product }),
         });
      }
   }

   private async refresh() {
      await ordersStore.fetchCartAsync({});
   }

   private orderNumberYear(order: Order) {
      if (!order) { return ''; }
      return order.documentNumber + '/' + order.year;
   }
}
